import * as React from 'react'
import {Box, TextField, FormControl, Select, MenuItem, Typography, InputAdornment} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import {filterBarSx} from '../../../../styles/patterns'
import {FONT, SIZE} from '../../../../styles/foundation/typography'
import {SUBTLE} from '../../../../styles/foundation/colors'
import {int} from '../format'
import {FILTERS} from './rows'

// Menu is portalled (outside the shell's zoom), so it paints at full size
// unless told to match this Select's own 9.6px closed-state text.
const selectMenuProps = {PaperProps: {sx: {'& .MuiMenuItem-root': {fontSize: SIZE['2xs']}}}}

/** Every keystroke re-filters the whole tab (up to 111 rows) and re-renders a sparkline per visible row — long enough to be worth not doing on every character. */
const DEBOUNCE_MS = 200

/**
 * Weight Freeze (v2) — Initiate: the candidate table's search + filter bar.
 *
 * `q`/`filter` are OWNED BY THE PARENT (they drive filterRows/tabStats), so
 * this component holds only a local typing draft and debounces it into
 * `onQChange`. The mockup filtered on every keystroke with no debounce; this
 * is the one deliberate deviation named in the plan's own self-review.
 */
export default function IniFilterBar({q, filter, onQChange, onFilterChange, resultCount, placeholder}) {
  const [draft, setDraft] = React.useState(q || '')

  // The parent can reset `q` out from under this component (a tab switch, a
  // "Clear filters" click) — when that happens the local draft must follow,
  // never the other way: the debounce timer below is the only thing allowed
  // to drive `q` from a keystroke.
  React.useEffect(() => {
    setDraft(q || '')
  }, [q])

  React.useEffect(() => {
    if (draft === (q || '')) return
    const timer = setTimeout(() => onQChange(draft), DEBOUNCE_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft])

  return (
    <Box sx={{...filterBarSx, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 0}}>
      <TextField
        size='small'
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder={placeholder}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <SearchIcon sx={{fontSize: 16, color: SUBTLE}} />
            </InputAdornment>
          ),
        }}
        inputProps={{'aria-label': placeholder || 'Search rows'}}
        sx={{fontFamily: FONT, fontSize: SIZE['2xs'], minWidth: 240}}
      />

      <FormControl size='small' sx={{minWidth: 210}}>
        <Select
          value={filter}
          onChange={(event) => onFilterChange(event.target.value)}
          inputProps={{'aria-label': 'Filter rows'}}
          sx={{fontFamily: FONT, fontSize: SIZE['2xs']}}
          MenuProps={selectMenuProps}
        >
          {FILTERS.map((f) => <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>)}
        </Select>
      </FormControl>

      <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, ml: 'auto', whiteSpace: 'nowrap'}}>
        {int(resultCount)} rows, highest variance first
      </Typography>
    </Box>
  )
}
